import { Grid, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import { ResourceCard } from '../components/ResourceCard';
import { getRootComponentPath, RootComponents } from './RootPage';
import {
  Routes,
  useAnalytics,
  METRICS_ARTICLES_ACTIVITY_ACCESS,
  METRICS_ARTICLES_PROPERTY_SOURCE,
  METRICS_ARTICLES_PROPERTY_SOURCE_RESOURCES,
  METRICS_EVENTS_ACTIVITY_ACCESS,
  METRICS_EVENTS_PROPERTY_SOURCE,
  METRICS_EVENTS_PROPERTY_SOURCE_RESOURCES,
  METRICS_DIM_ACTIVITY_ACCESS,
  METRICS_DIM_PROPERTIES_SOURCE,
  METRICS_DIM_PROPERTIES_SOURCE_RESOURCES,
} from '@xolv-mybrightlink/common';
import { useResourcesMenuItems } from '../hooks/useResourcesMenuItems';

export type ResourceItem = {
  resourceType: RootComponents;
  gradientStartColor: string;
  gradientEndColor: string;
  labelKey: string;
  backgroundImage: string;
  title?: string;
};

export const ResourcesPage = () => {
  const navigate = useNavigate();
  const visibleResources = useResourcesMenuItems();
  const resources: ResourceItem[] = [
    {
      resourceType: RootComponents.Parent_Led_Curriculum,
      gradientStartColor: '#c6eafa',
      gradientEndColor: '#daddf8',
      labelKey: 'sidebar.menu.parent-led-curriculum',
      title: 'resources.parent-led',
      backgroundImage:
        '/public/assets/images/illustration_parent_led_curriculum.svg',
    },
    {
      resourceType: RootComponents.Dimension_Parent_Class,
      gradientStartColor: '#f6dfeb',
      gradientEndColor: '#f9ded9',
      labelKey: 'sidebar.menu.dimension-parent-class',
      backgroundImage: '/public/assets/images/illustration_dimension_class.svg',
    },
    {
      resourceType: RootComponents.Articles,
      gradientStartColor: '#a0f1ea',
      gradientEndColor: '#93dbb0',
      labelKey: 'sidebar.menu.articles',
      backgroundImage: '/public/assets/images/illustration_articles.svg',
    },
    {
      resourceType: RootComponents.Events,
      gradientStartColor: '#c0e5fd',
      gradientEndColor: '#79cee9',
      labelKey: 'sidebar.menu.events',
      backgroundImage: '/public/assets/images/illustration_events.svg',
    },
  ].filter((resource) => visibleResources.includes(resource.resourceType));

  const analytics = useAnalytics();

  const trackItemClick = (component: RootComponents) => {
    if (component === RootComponents.Articles) {
      analytics.trackAction(METRICS_ARTICLES_ACTIVITY_ACCESS, {
        [METRICS_ARTICLES_PROPERTY_SOURCE]:
          METRICS_ARTICLES_PROPERTY_SOURCE_RESOURCES,
      });
    } else if (component === RootComponents.Events) {
      analytics.trackAction(METRICS_EVENTS_ACTIVITY_ACCESS, {
        [METRICS_EVENTS_PROPERTY_SOURCE]:
          METRICS_EVENTS_PROPERTY_SOURCE_RESOURCES,
      });
    } else if (component === RootComponents.Dimension_Parent_Class) {
      analytics.trackAction(METRICS_DIM_ACTIVITY_ACCESS, {
        [METRICS_DIM_PROPERTIES_SOURCE]:
          METRICS_DIM_PROPERTIES_SOURCE_RESOURCES,
      });
    }
  };

  const openPage = (component: RootComponents) => {
    trackItemClick(component);
    navigate(Routes.resources.child(getRootComponentPath(component)));
  };

  return (
    <Stack>
      <Typography
        variant={'h6'}
        sx={{
          fontWeight: 500,
          mb: 12.5,
          mt: 11.5,
          ml: 10,
        }}
      >
        <FormattedMessage id='sidebar.menu.resources' />
      </Typography>
      <Grid
        container
        spacing={10}
        columns={{ md: 1, lg: 2, xl: 3 }}
        rowSpacing={20}
        sx={{
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 10, md: 5, lg: 10 },
        }}
      >
        {' '}
        {resources.map((resource, index) => (
          <ResourceCard
            key={resource.resourceType}
            onClick={() => openPage(resource.resourceType)}
            item={resource}
            index={index}
            totalLength={resources.length}
          />
        ))}
      </Grid>
    </Stack>
  );
};
